// components/UserActivity.jsx — CloudTrail "User Activity": rekonstruksi apa yang dilakukan satu IAM user.
// Sumber: GET /api/aws-cloudtrail/user-activity (tanpa user = daftar user; ?user= = timeline).
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../context/AuthContext';
import { UserCircle, AlertTriangle, Trash2, Globe2, Box, ChevronDown, ChevronRight, Search } from 'lucide-react';

const DESTRUCTIVE = /^(Delete|Terminate|Release|Revoke|Deregister|Stop|Reboot|Detach|Disassociate|Remove)/;
const CREATE = /^(Create|Run|Allocate|Authorize|Register|Put|Start|Associate|Request)/;
const fmtT = (s) => { const d = new Date(s); return isNaN(d) ? s : d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); };
const fmtDT = (s) => { const d = new Date(s); return isNaN(d) ? (s || '—') : d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); };
const fmtD = (s) => new Date(s + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });

function Stat({ label, value, tone }) {
  const c = { rose: 'text-rose-600', amber: 'text-amber-600', blue: 'text-blue-600' }[tone] || 'text-slate-800';
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-bold tabular-nums ${c}`}>{value}</div>
    </div>
  );
}

export default function UserActivity() {
  const [users, setUsers] = useState([]);
  const [user, setUser] = useState('');
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState({});
  const [onlyIssues, setOnlyIssues] = useState(false);

  useEffect(() => {
    api.get('/api/aws-cloudtrail/user-activity').then(r => setUsers(r.data.users || [])).catch(e => setErr(e.response?.data?.error || e.message));
  }, []);

  useEffect(() => {
    if (!user) { setData(null); return; }
    setLoading(true); setErr('');
    api.get('/api/aws-cloudtrail/user-activity', { params: { user } })
      .then(r => { setData(r.data); const o = {}; (r.data.days || []).forEach(d => { o[d.date] = true; }); setOpen(o); })
      .catch(e => setErr(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [user]);

  const shownUsers = useMemo(() => users.filter(u => !q || u.user.toLowerCase().includes(q.toLowerCase())), [users, q]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <div className="bg-white border border-slate-200 rounded-xl p-3 h-fit">
        <div className="relative mb-2">
          <Search className="absolute left-2 top-2.5 text-slate-400" style={{ width: 14, height: 14 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search user…"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded-lg" />
        </div>
        <div className="max-h-[560px] overflow-y-auto divide-y divide-slate-100">
          {shownUsers.map(u => (
            <button key={u.user} onClick={() => setUser(u.user)}
              className={`w-full text-left px-2 py-2 rounded-lg ${user === u.user ? 'bg-blue-50' : 'hover:bg-slate-50'}`}>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-800 truncate">
                <UserCircle style={{ width: 14, height: 14 }} className="text-slate-400 flex-shrink-0" /> {u.user}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {u.writes} writes · {u.errors} errors · {u.ips} IPs · last {fmtDT(u.last)}
              </div>
            </button>
          ))}
          {!shownUsers.length && <div className="text-sm text-slate-400 p-2">No users.</div>}
        </div>
      </div>

      <div className="min-w-0">
        {err && <div className="text-sm text-rose-600 mb-2">{err}</div>}
        {!user && <div className="bg-white border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500 text-sm">
          Select an IAM user to reconstruct what they did — timeline per day, resources touched, and source IPs.
        </div>}
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {data && !loading && (
          <>
            <div className="mb-3">
              <div className="text-lg font-bold text-slate-800">{data.user}</div>
              <div className="text-xs text-slate-500 break-all">{data.summary.arn || ''}</div>
              <div className="text-xs text-slate-500">{fmtDT(data.summary.first)} → {fmtDT(data.summary.last)}</div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <Stat label="Write events" value={data.summary.total} tone="blue" />
              <Stat label="Failed" value={data.summary.errors} tone="amber" />
              <Stat label="Destructive" value={data.summary.destructive} tone="rose" />
              <Stat label="Source IPs" value={data.summary.ips} />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-3 mb-4">
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-2"><Box style={{ width: 14, height: 14 }} /> Resources touched ({data.resources.length})</div>
                <div className="max-h-64 overflow-y-auto text-xs space-y-1.5">
                  {data.resources.map(r => (
                    <div key={r.type + r.name} className="border-b border-slate-100 pb-1">
                      <div className="font-mono text-slate-800 break-all">{r.name}</div>
                      <div className="text-slate-500">{String(r.type || '').replace('AWS::', '')} · {r.actions.map(a => `${a.k}×${a.n}`).join(', ')}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-700 mb-2"><Globe2 style={{ width: 14, height: 14 }} /> Source IPs</div>
                <div className="text-xs space-y-1.5">
                  {data.ips.map(p => (
                    <div key={p.ip} className="border-b border-slate-100 pb-1">
                      <div className="flex justify-between"><span className="font-mono text-slate-800">{p.ip}</span><span className="tabular-nums text-slate-600">{p.n}</span></div>
                      <div className="text-slate-500 truncate">{fmtDT(p.first)} → {fmtDT(p.last)} · {p.agent}</div>
                    </div>
                  ))}
                </div>
                <div className="text-sm font-semibold text-slate-700 mt-3 mb-1">Services</div>
                <div className="flex flex-wrap gap-1">
                  {data.services.map(s => <span key={s.k} className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">{s.k} {s.n}</span>)}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-semibold text-slate-700">Timeline</div>
              <label className="text-xs text-slate-600 flex items-center gap-1">
                <input type="checkbox" checked={onlyIssues} onChange={e => setOnlyIssues(e.target.checked)} /> Failed & destructive only
              </label>
            </div>
            {data.days.map(d => {
              const evs = d.events.filter(e => !onlyIssues || e.error || DESTRUCTIVE.test(e.name));
              return (
                <div key={d.date} className="bg-white border border-slate-200 rounded-xl mb-2">
                  <button onClick={() => setOpen(o => ({ ...o, [d.date]: !o[d.date] }))}
                    className="w-full flex items-center justify-between px-3 py-2 text-sm font-semibold text-slate-700">
                    <span className="flex items-center gap-1">{open[d.date] ? <ChevronDown style={{ width: 14, height: 14 }} /> : <ChevronRight style={{ width: 14, height: 14 }} />} {fmtD(d.date)}</span>
                    <span className="text-xs font-normal text-slate-500">{d.events.length} events</span>
                  </button>
                  {open[d.date] && (
                    <div className="border-t border-slate-100 px-3 py-2 space-y-1">
                      {evs.map((e, i) => {
                        const destr = DESTRUCTIVE.test(e.name);
                        const dot = e.error ? 'bg-amber-500' : destr ? 'bg-rose-500' : CREATE.test(e.name) ? 'bg-emerald-500' : 'bg-blue-400';
                        return (
                          <div key={i} className="flex gap-2 text-xs">
                            <span className="w-16 flex-shrink-0 tabular-nums text-slate-500">{fmtT(e.time)}</span>
                            <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
                            <div className="min-w-0">
                              <span className={`font-semibold ${destr ? 'text-rose-700' : 'text-slate-800'}`}>{e.name}</span>
                              <span className="text-slate-400"> · {e.source} · {e.ip}</span>
                              {e.resources.length > 0 && <div className="font-mono text-slate-600 break-all">{e.resources.filter(r => !String(r).startsWith('arn:')).join(', ')}</div>}
                              {e.error && <div className="text-amber-700 flex items-start gap-1"><AlertTriangle style={{ width: 12, height: 12 }} className="mt-0.5 flex-shrink-0" />{e.error}{e.errorMessage ? ` — ${e.errorMessage}` : ''}</div>}
                            </div>
                          </div>
                        );
                      })}
                      {!evs.length && <div className="text-xs text-slate-400">Nothing to show.</div>}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><Trash2 style={{ width: 11, height: 11 }} /> Shows AWS API calls only — commands typed inside a server via SSM are not in CloudTrail.</div>
          </>
        )}
      </div>
    </div>
  );
}
